import axios from "axios";
import { ExpiredTokenError } from "errors/ExpiredTokenError";
import { FinishedOccurrenceError } from "errors/FinishedOccurrenceError";
import { IFilterWhereOptions } from "hooks/useDataList";
import { ISuperficialAddress } from "interfaces/Address";
import { IFilterData } from "interfaces/FilterProps";
import { FindOptionsOrder } from "interfaces/IQuery";
import {
  ICreateOccurrence,
  IOccurrence,
  IOccurrencesFilteredByStatus,
  IStatistics,
  IUpdateOccurrence,
} from "interfaces/Occurrence";
import { ISuperficialIVehicleProps } from "interfaces/Vehicle";
import { useQuery } from "react-query";
import { client } from "./api";
import { ICancelOccurrenceProps, IDeleteOccRegisterProps } from "./handles/OccurrenceHandles";
import { HistoricItem } from "interfaces/Historic";

export type IGetOccurrencesResponse = {
  item: {
    progress: number;
    waiting: number;
    finished: number;
    manual: number;
    monitor: number;
    all: number;
    withoutPoliceman: number;
    occurrences: IOccurrence[];
  };
  status: boolean;
};

export type IGetHistoricResponse = {
  item: {
      count: {
      all: number
      progress: number
      await: number
      finish: number
      queryCount: number
    }
    items: HistoricItem[]
  },
  status: boolean;
};

export type IGetOccurrenceResponse = {
  item: IOccurrence;
  status: boolean;
};

export async function getOccurrencesService({
  params,
  nots,
  order,
  ...rest
}: filter<IOccurrence>): Promise<IGetOccurrencesResponse> {
  const api = client();
  const response = await api.get<IGetOccurrencesResponse>("occurrences", {
    params: {
      ...rest,
      order: JSON.stringify(order),
      params: JSON.stringify(params),
      nots: JSON.stringify(nots),
    },
  });

  return response.data;
}
export async function getHistoricService({
  params,
  nots,
  order,
  ...rest
}: filter<HistoricItem>): Promise<IGetHistoricResponse> {
  const api = client();
  const response = await api.get<IGetHistoricResponse>("occurrences/historic", {
    params: {
      ...rest,
      order: JSON.stringify(order),
      params: JSON.stringify(params),
      nots: JSON.stringify(nots),
    },
  });

  return response.data;
}
export function useOccurrence({ filter }: IUseParams<IOccurrence>) {
  return useQuery(
    ["occurrences", { filter }],
    () => getOccurrencesService(filter),
    {
      staleTime: 1000 * 60 * 5,
      refetchInterval: 30000
    },
  );
}

export function useHistoric({ filter }: IUseParams<HistoricItem>) {
  return useQuery(
    ["historic", { filter }],
    () => getHistoricService(filter),
    {
      staleTime: 1000 * 60 * 5,
      refetchInterval: 30000
    },
  );
}
interface filter<T> {
  nots?: { key: string, value: string }[]
  since?: Date
  until?: Date
  page?: number
  notPage?: boolean
  size?: number
  order?: FindOptionsOrder<T>
  params?: IFilterWhereOptions<T>
}
interface IUseParams<T> {
  filter: filter<T>;
}

type IUseOptions<IOccurrence> = {
  initialData?: IFilterData<IOccurrence>;
};

export async function getOccurrencesIdService(
  id: string
): Promise<IGetOccurrenceResponse> {
  const api = client();
  const response = await api.get<IGetOccurrenceResponse>(`occurrences/one`, {
    params: {
      where: { id },
      relations: {
        occurrencesSuperficialAddresses: { superficialAddress: true },
        client: true,
        attendant: true,
        superficialVehicle: true,
        policemen: { policeman: { user: true } },
        report: {
          attachments: true,
          superficialPoliceman: true
        }
      },
      join: {
        alias: "occurrence",
        leftJoinAndSelect: {
          OccurrencesSuperficialAddresses:
            "occurrence.occurrencesSuperficialAddresses",
          address: "OccurrencesSuperficialAddresses.superficialAddress",
          subClient: "address.superficialClient",
          client: "occurrence.client",
          attendant: "occurrence.attendant",
          vehicle: "occurrence.superficialVehicle",
          occurrencePoliceman: "occurrence.policemen",
          policeman: "occurrencePoliceman.policeman",
          user: "policeman.user",
          report: "occurrence.report",
          attachments: "report.attachments",
          superficialPoliceman: "report.superficialPoliceman",
          tenant: "occurrence.tenant",
        },
      },
    },
  });
  return response.data;
}

export async function getSharedOccurrenceData(
  authorizationToken: string
): Promise<IGetOccurrenceResponse | void> {
  const api = client();
  try {
    const response = await api.get<IGetOccurrenceResponse>(
      "/occurrences/share",
      {
        headers: { Authorization: `Bearer ${authorizationToken}` },
      }
    );

    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new ExpiredTokenError();
    }

    if (axios.isAxiosError(error) && error.response?.status === 400) {
      throw new FinishedOccurrenceError();
    }
  }
}

export async function getOccurrenceExpireToken(id: string): Promise<string> {
  const api = client();
  const response = await api.post<{ status: boolean; token: string }>(
    `occurrences/share`,
    {
      occurrenceId: id,
    }
  );

  if (!response.data.status) {
    throw new Error("Unexpected error occurred");
  }

  const token = response.data.token;

  return token;
}

export type IPostOccurrencesResponse = {
  data: {
    occurrence?: IOccurrence;
    response?: IOccurrence;
    address: ISuperficialAddress;
  };
  status: boolean;
};
export async function deleteOccurrence({
  occurrence,
  id
}: IDeleteOccRegisterProps) {
  const api = client();
  if(id){
    const response = await api.delete(`occurrences/${id}`);
    return response.data;
  }
  const response = await api.delete(`occurrences/${occurrence?.id}`);
  return response.data;
}

export async function cancelOccurrence({
  occurrence,
  reportId
}: ICancelOccurrenceProps) {
  if (!occurrence) throw new Error();

  const api = client();
  const response = await api.patch(`occurrences/cancel`, {
    id: occurrence.id,
    reportId,
  })
  return response.data;
}

export async function getManualOccurrence(id: string) {
  if (!id) return;

  const api = client();
  const response = await api.get<IGetOccurrenceResponse>(`occurrences/one`, {
    params: {
      where: { id },
      relations: {
        attendant: true,
        client: { product: true },
        occurrencesSuperficialAddresses: { superficialAddress: true },
        superficialVehicle: { superficialClient: true },
        policemen: { policeman: { user: { bankAccount: true } } },
        report: {
          policeman: { user: { bankAccount: true } },
          superficialPoliceman: true,
          attachments: true
        },
        comments: { attendant: true },
        history: { attendant: true }
      },
    },
  });
  return response.data;
}

export async function createOccurrenceService({
  lastCoords,
  patrimonialType,
  postalCode,
  street,
  number,
  district,
  uf,
  city,
  coordX,
  coordY,
  numberBO,
  superficialClient,
  lastCoordX,
  lastCoordY,
  reference,
  isManual,
  workType,
  superficialVehicle,
  superficialAddressId,
  Client,
  superficialClientId,
  policemen
}: ICreateOccurrence) {
  if (!lastCoordX) {
    lastCoordX = coordX;
    lastCoordY = coordY;
  }
  const api = client();

  if (lastCoords && lastCoords[0][0]) {
    return await api.post<IPostOccurrencesResponse>("occurrences", {
      occurrence: {
        client: { id: Client.id },
        patrimonialType,
        workType,
        lastCoordX,
        isManual,
        lastCoordY,
        numberBO,
        stringLastCoords: JSON.stringify(lastCoords),
      },
      superficialAddress: {
        id: superficialAddressId,
        postalCode,
        street,
        number,
        district,
        uf,
        city,
        coordX,
        coordY,
        reference,
        superficialClient,
      },
      policemen,
      superficialVehicle,
      superficialClientId,
    });
  }

  return await api.post<IPostOccurrencesResponse>("occurrences", {
    occurrence: {
      client: { id: Client.id },
      workType,
      lastCoordX,
      lastCoordY,
      numberBO,
      patrimonialType,
      isManual,
    },
    superficialAddress: {
      id: superficialAddressId,
      postalCode,
      street,
      number,
      district,
      uf,
      city,
      coordX,
      coordY,
      reference,
      superficialClient,
    },
    policemen,
    superficialVehicle,
    superficialClientId,
  });
}

export async function createManualOccurrenceService({
  lastCoords,
  patrimonialType,
  postalCode,
  street,
  number,
  district,
  uf,
  city,
  coordX,
  coordY,
  numberBO,
  superficialClient,
  lastCoordX,
  lastCoordY,
  reference,
  workType,
  superficialVehicle,
  superficialAddressId,
  Client,
  isManual,
}: ICreateOccurrence) {
  if (!lastCoordX) {
    lastCoordX = coordX;
    lastCoordY = coordY;
  }
  const api = client();
  if (lastCoords && lastCoords[0][0]) {
    return await api.post<IPostOccurrencesResponse>("occurrences", {
      occurrence: {
        client: { id: Client.id },
        patrimonialType,
        workType,
        lastCoordX,
        lastCoordY,
        numberBO,
        stringLastCoords: JSON.stringify(lastCoords),
        isManual,
      },
      superficialAddress: {
        id: superficialAddressId,
        postalCode,
        street,
        number,
        district,
        uf,
        city,
        coordX,
        coordY,
        reference,
        superficialClient,
      },
      superficialVehicle,
    });
  }
  return await api.post<IPostOccurrencesResponse>("occurrences", {
    occurrence: {
      client: { id: Client.id },
      workType,
      lastCoordX,
      lastCoordY,
      numberBO,
      patrimonialType,
    },
    superficialAddress: {
      id: superficialAddressId,
      postalCode,
      street,
      number,
      district,
      uf,
      city,
      coordX,
      coordY,
      reference,
      superficialClient,
    },
    superficialVehicle,
  });
}

export async function updateOccurrenceService(data: IUpdateOccurrence) {
  const api = client();
  for (const key in data) {
    if (data[key as keyof IUpdateOccurrence] === null) {
      delete data[key as keyof IUpdateOccurrence];
    }
  }
  const {
    id,
    lastCoords,
    patrimonialType,
    superficialClient,
    numberBO,
    lastCoordX,
    lastCoordY,
    postalCode,
    street,
    number,
    district,
    uf,
    city,
    reference,
    coordX,
    coordY,
    workType,
    superficialAddressId,
    superficialVehicle,
  } = data;
  delete superficialVehicle?.occurrence;
  for (const key in superficialVehicle) {
    if (superficialVehicle[key as keyof ISuperficialIVehicleProps] === null) {
      delete superficialVehicle[key as keyof ISuperficialIVehicleProps];
    }
  }

  const response = await api.patch("occurrences", {
    occurrence: {
      id,
      superficialClient,
      workType,
      lastCoordX,
      lastCoordY,
      numberBO,
      patrimonialType,
      stringLastCoords: lastCoords
        ? JSON.stringify(lastCoords)
        : JSON.stringify([[lastCoordX, lastCoordY]]),
    },
    superficialAddress: {
      id: superficialAddressId,
      postalCode,
      street,
      number,
      district,
      uf,
      city,
      coordX,
      coordY,
      reference,
    },
    superficialVehicle,
  });
  return response.data;
}

export async function createSuperficialVehicleService({
  brand,
  color,
  description,
  model,
  plate,
  occurrence,
  superficialClient,
}: ISuperficialIVehicleProps) {
  const api = client();
  return await api.post("superficial/vehicles", {
    brand,
    color,
    description,
    model,
    plate,
    occurrence,
    superficialClient,
  });
}
export async function updateSuperficialVehicleService({
  id,
  brand,
  color,
  description,
  model,
  plate,
  superficialClient,
  occurrence,
}: ISuperficialIVehicleProps) {
  const api = client();
  return await api.patch("superficial/vehicles", {
    id,
    brand,
    color,
    description,
    model,
    plate,
    occurrence,
    superficialClient,
  });
}

type IGetOccurrencesStatisticsResponse = {
  item: IStatistics;
  status: boolean;
};

async function getOccurrencesStatisticsService(): Promise<IGetOccurrencesStatisticsResponse> {
  const api = client();
  const response = await api.get<IGetOccurrencesStatisticsResponse>(
    "occurrences/statistic"
  );
  console.log("🚀 ~ file: OccurrenceService.ts:508 ~ getOccurrencesStatisticsService ~ response:", response)
  if (!response) {
    throw new Error();
  }
  return response.data;
}

type UseOccurrencesStatisticsOptions = {
  initialData: {
    item: IStatistics;
    status: boolean;
  };
};

export function useOccurrencesStatistics(
  options?: UseOccurrencesStatisticsOptions
) {
  return useQuery(
    ["occurrences statistics"],
    () => getOccurrencesStatisticsService(),
    {
      staleTime: 1000 * 60 * 5,
      ...options,
    }
  );
}

export function GroupByStatus(occurrences: IOccurrence[]) {
  return occurrences.reduce((acc: IOccurrencesFilteredByStatus, item) => {
    if (!acc[item["status"]]) acc[item["status"]] = [];
    acc[item["status"]]?.push(item);
    return acc;
  }, {});
}
